import React, {useState, useEffect} from 'react'
import CustomBox from '../../components/common/CustomBox'
import Grid from '@mui/material/Grid'
import CloseIcon from '@mui/icons-material/Close'
import Typography from '@mui/material/Typography'
import {
  InputError,
  InlineErrorWrapper,
  StyledCircularProgress,
  StyledTooltip,
  TemporaryToggle,
  StyledToolTipIconButton
} from './UserManagementStyle'
import {StyledDialogTitle, StyledIconButton} from './RemoveUser/RemoveUserStyles'
import {
  StyledTextField,
  StyledDialogContent,
  StyledGrid,
  StyledDivFooter,
  SaveButton,
  CancelButton,
  StyledDialog,
  StyledTemporaryAccessRow,
  StyledInputTitleInline,
  StyledEndDateWrapper
} from './EditUserDetails/EditUserStyle'
import {useAddUser} from '../api/addUser'
import {useParams} from 'react-router-dom'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessage from '../common/Snackbar/SnackbarMessages.json'
import {handleInput, handleEmailAddressInput} from '../common/Helpers/validator'
import isJsonString from '../common/Helpers/checkJsonString'
import {useSwitchState} from './SwitchState'
import StartDatePickerComponent from './StartDatePickerComponent'
import EndDatePickerComponent from './EndDatePickerComponent'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PopupMessages from '../common/PopupMessages/PopupMessages.json'
import buttonTitles from '../../appConfig/buttonTitles.config.json'

const CreateUserComponent = ({
  userData,
  setUserAdded,
  isUserManagement,
  isCompleted,
  setInputValue,
  clientUserData,
  showSectionDivider,
  isClientComponent,
  open,
  setOpen
}) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [errorFirstNameText, setErrorFirstNameText] = React.useState('')
  const [errorFirstNameTextEnabled, setErrorFirstNameTextEnabled] = useState(false)
  const [errorLastNameText, setErrorLastNameText] = React.useState('')
  const [errorLastNameTextEnabled, setErrorLastNameTextEnabled] = useState(false)
  const [errorEmailAddressText, setErrorEmailAddressText] = React.useState('')
  const [errorEmailAddressTextEnabled, setErrorEmailAddressTextEnabled] = useState(false)
  const [showNotification, setShowNotification] = React.useState(false)
  // sonarlint fix line below
  // const [createUser, {data: data, loading: loading, error: error}] = addUser()
  const [createUser, {data, loading, error}] = useAddUser()
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [inlineError, setInlineError] = useState('')
  const [errorSnapshot, setErrorSnapshot] = useState(null)
  const {checked, handleTempSwitch} = useSwitchState()
  const {projectId} = useParams()
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const tempUserTooltipText = 'Temporary users have an Access End Date.'
  useEffect(() => {
    if (!checked) {
      setEndDate(null)
    }
  }, [checked])

  useEffect(() => {
    if (open) {
      setInlineError('')
      setErrorSnapshot(null)
    }
  }, [open])

  React.useEffect(() => {
    if (clientUserData) {
      setFirstName('')
      setLastName('')
      setEmailAddress('')
      setErrorFirstNameTextEnabled(false)
      setErrorLastNameTextEnabled(false)
      setErrorEmailAddressTextEnabled(false)
    }
  }, [clientUserData])

  React.useEffect(() => {
    if (isClientComponent) {
      setInputValue(
        firstName,
        lastName,
        emailAddress,
        errorFirstNameTextEnabled || errorLastNameTextEnabled || errorEmailAddressTextEnabled,
        false
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, emailAddress, isClientComponent])

  const formattedStartDate = dayjs(startDate)?.format('YYYY-MM-DD')
  const formattedEndDate = dayjs(endDate)?.format('YYYY-MM-DD')
  const hasChangedAfterError =
    errorSnapshot?.firstName !== firstName ||
    errorSnapshot?.lastName !== lastName ||
    errorSnapshot?.emailAddress !== emailAddress ||
    errorSnapshot?.startDate !== formattedStartDate ||
    errorSnapshot?.endDate !== formattedEndDate ||
    errorSnapshot?.checked !== checked

  // Clear inline error when user makes changes after error
  useEffect(() => {
    if (inlineError && hasChangedAfterError) {
      setInlineError('')
      setErrorSnapshot(null)
    }
  }, [hasChangedAfterError, inlineError])

  const handleAdd = () => {
    setInlineError('')
    setErrorSnapshot(null)
    setShowNotification(false)
    setUserAdded(false)
    const variables = {
      groupId: projectId,
      email: emailAddress.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      startDate: formattedStartDate,
      isTemporary: checked
    }

    if (checked) {
      variables.endDate = formattedEndDate
    }

    createUser({
      variables
    })
  }

  React.useEffect(() => {
    if (!error && data) {
      setFirstName('')
      setLastName('')
      setEmailAddress('')
      setStartDate(null)
      setEndDate(null)
      setErrorSnapshot(null)
      if (checked) {
        handleTempSwitch()
      }
      setUserAdded(true)
      setOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  React.useEffect(() => {
    if (error) {
      const {message} = error
      const formatMessage = message?.replaceAll("'", '"')
      if (isJsonString(formatMessage) && JSON.parse(formatMessage).status_code === 409) {
        setInlineError(JSON.parse(formatMessage).message)
        setErrorSnapshot({
          firstName,
          lastName,
          emailAddress,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          checked
        })
      } else {
        setSnackbarMessage(SnackbarMessage?.addUserError)
        setShowNotification(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])
  const handleFirstNameBlur = () => {
    const trimmed = firstName.trim()
    const capitalizedFirstName = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    setFirstName(capitalizedFirstName)
  }
  const handleLastNameBlur = () => {
    const trimmed = lastName.trim()
    const capitalizedLastName = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    setLastName(capitalizedLastName)
  }
  const handleEmailAddressBlur = () => {
    setEmailAddress(emailAddress.trim())
  }

  const [isToolTipOpen, setIsToolTipOpen] = useState(false)
  const handleClickAway = () => {
    setIsToolTipOpen(false)
  }

  const handleClose = () => {
    setFirstName('')
    setLastName('')
    setEmailAddress('')
    setStartDate(null)
    setEndDate(null)
    setErrorFirstNameTextEnabled(false)
    setErrorLastNameTextEnabled(false)
    setErrorEmailAddressTextEnabled(false)
    setInlineError('')
    setErrorSnapshot(null)
    if (checked) {
      handleTempSwitch()
    }
    setOpen(false)
  }

  return (
    <div>
      {isUserManagement && (
        <StyledDialog maxWidth='md' onClose={handleClose} aria-labelledby='create-user-dialog-title' open={open}>
          <StyledDialogTitle id='create-user-dialog-title'>
            {PopupMessages.addUserTitle}
            <StyledIconButton title={buttonTitles.popupNotification} onClick={handleClose} aria-label='close'>
              <CloseIcon variant='popOverCloseIcon' />
            </StyledIconButton>
          </StyledDialogTitle>
          <StyledDialogContent id='create-user-header' dividers>
            <StyledGrid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <StyledTextField
                  required
                  label='First Name'
                  variant='outlined'
                  fullWidth
                  aria-label='Enter first name'
                  id='enter-first-name'
                  disabled={loading || !isCompleted}
                  error={errorFirstNameTextEnabled}
                  name='firstname'
                  value={firstName}
                  placeholder='First name'
                  onChange={handleInput(setFirstName, setErrorFirstNameTextEnabled, setErrorFirstNameText, true)}
                  onBlur={handleFirstNameBlur}
                  InputLabelProps={{shrink: true}}
                />
                <CustomBox tabIndex={0} hidden={!errorFirstNameTextEnabled}>
                  <InputError id='first-name-error' aria-live='polite'>
                    {errorFirstNameTextEnabled && errorFirstNameText}
                  </InputError>
                </CustomBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <StyledTextField
                  required
                  label='Last Name'
                  variant='outlined'
                  fullWidth
                  aria-label='Enter last name'
                  id='enter-last-name'
                  disabled={loading || !isCompleted}
                  error={errorLastNameTextEnabled}
                  name='lastname'
                  value={lastName}
                  placeholder='Last name'
                  onChange={handleInput(setLastName, setErrorLastNameTextEnabled, setErrorLastNameText, true)}
                  onBlur={handleLastNameBlur}
                  InputLabelProps={{shrink: true}}
                />
                <CustomBox tabIndex={0} hidden={!errorLastNameTextEnabled}>
                  <InputError id='last-name-error' aria-live='polite'>
                    {errorLastNameTextEnabled && errorLastNameText}
                  </InputError>
                </CustomBox>
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  required
                  label='Email Address'
                  variant='outlined'
                  fullWidth
                  aria-label='Enter email address'
                  id='enter-email-address'
                  disabled={loading || !isCompleted}
                  value={emailAddress}
                  error={errorEmailAddressTextEnabled}
                  type='email'
                  placeholder='Email Address'
                  onChange={handleEmailAddressInput(
                    setEmailAddress,
                    setErrorEmailAddressTextEnabled,
                    setErrorEmailAddressText,
                    userData
                  )}
                  onBlur={handleEmailAddressBlur}
                  InputLabelProps={{shrink: true}}
                />
                <CustomBox tabIndex={0} hidden={!errorEmailAddressTextEnabled}>
                  <InputError id='email-error' aria-live='polite'>
                    {errorEmailAddressTextEnabled && errorEmailAddressText}
                  </InputError>
                </CustomBox>
                {inlineError && (
                  <InlineErrorWrapper role='alert' aria-live='assertive'>
                    <ErrorOutlineIcon />
                    <span>{inlineError}</span>
                  </InlineErrorWrapper>
                )}
              </Grid>
              <Grid item xs={12}>
                <StyledTemporaryAccessRow>
                  <StyledInputTitleInline>
                    Temporary Access{' '}
                    <StyledTooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={6000}
                      title={tempUserTooltipText}
                      open={isToolTipOpen}
                      onClickAway={handleClickAway}
                      disableHoverListener={true}
                      disableFocusListener={true}
                      aria-describedby='temporary-date-field-tooltip'
                    >
                      <StyledToolTipIconButton
                        onClick={() => setIsToolTipOpen(!isToolTipOpen)}
                        id='temporary-date-field-tooltip'
                        data-testid='temporary-date-field-tooltip'
                        aria-label={isToolTipOpen ? tempUserTooltipText : 'Toggle Temporary End Date Field Tooltip'}
                        aria-controls='temporary-date-field-tooltip'
                        aria-expanded={isToolTipOpen}
                      >
                        <InfoOutlinedIcon variant='primaryIcon' />
                      </StyledToolTipIconButton>
                    </StyledTooltip>
                  </StyledInputTitleInline>
                  <Typography variant='body2' color='textSecondary'>
                    Off
                  </Typography>
                  <TemporaryToggle
                    data-testid='Switch-for-changing-user-type'
                    checked={checked}
                    onChange={handleTempSwitch}
                    inputProps={{'aria-label': 'Temporary Switch Button'}}
                    disabled={!isCompleted || loading}
                  />
                  <Typography variant='body2' color='textSecondary'>
                    On
                  </Typography>
                </StyledTemporaryAccessRow>
              </Grid>
              <Grid item xs={6} lg={6}>
                <StartDatePickerComponent
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  isProjectCompleted={isCompleted}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={6} lg={6}>
                <StyledEndDateWrapper checked={checked}>
                  <EndDatePickerComponent
                    startDate={startDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    checked={checked}
                    loading={loading}
                  />
                </StyledEndDateWrapper>
              </Grid>
            </StyledGrid>
            <StyledDivFooter>
              <CancelButton onClick={handleClose} disabled={loading}>
                Cancel
              </CancelButton>
              <SaveButton
                onClick={handleAdd}
                disabled={
                  !firstName ||
                  !lastName ||
                  !emailAddress ||
                  errorFirstNameTextEnabled ||
                  errorLastNameTextEnabled ||
                  errorEmailAddressTextEnabled ||
                  loading ||
                  !isCompleted ||
                  !startDate ||
                  (checked && !endDate) ||
                  (inlineError && !hasChangedAfterError)
                }
              >
                {loading ? <StyledCircularProgress size={20} /> : 'Add'}
              </SaveButton>
            </StyledDivFooter>
          </StyledDialogContent>
        </StyledDialog>
      )}
      <SnackbarComponent
        error={error}
        showNotification={showNotification}
        setShowNotification={setShowNotification}
        message={snackbarMessage}
        isCreateUser={'true'}
      />
    </div>
  )
}

CreateUserComponent.propTypes = {
  userData: PropTypes.object,
  setUserAdded: PropTypes.func,
  isUserManagement: PropTypes.bool,
  isCompleted: PropTypes.bool,
  setInputValue: PropTypes.func,
  clientUserData: PropTypes.object,
  showSectionDivider: PropTypes.bool,
  isClientComponent: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func
}

export default CreateUserComponent

/* Function Descriptions:
  - CreateUserComponent is a React component that allows the creation of a new user.
  - useEffect to reset endDate when temporary user toggle is unchecked.
  - useEffect to reset input fields and error states when clientUserData changes.
  - useEffect to update parent component's input values when isClientComponent is true.
  - handleAdd triggers the addUser mutation with user details and handles temporary user logic.
  - useEffect to reset form fields and toggle state on successful user creation.
  - useEffect to handle and display error messages from the addUser mutation.
  - handleFirstNameBlur trims and capitalizes the first name input.
  - handleLastNameBlur trims and capitalizes the last name input.
  - handleEmailAddressBlur trims the email address input.
  - handleClickAway closes the tooltip for temporary user information.
*/
